import { atom } from 'jotai';

export type DialogSentiment = 'success' | 'warning' | 'danger' | 'info';

export interface ConfirmationDialogState {
  isOpen: boolean;
  title: string;
  message: string;
  sentiment: DialogSentiment;
  confirmText: string;
  cancelText: string;
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void | Promise<void>;
  isLoading?: boolean;
}

// Base atom for the confirmation dialog state
export const confirmationDialogAtom = atom<ConfirmationDialogState>({
  isOpen: false,
  title: '',
  message: '',
  sentiment: 'info',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  onConfirm: () => {},
  onCancel: undefined,
  isLoading: false,
});

// Derived atoms for easier access to specific properties
export const isDialogOpenAtom = atom(get => get(confirmationDialogAtom).isOpen);

export const dialogTitleAtom = atom(get => get(confirmationDialogAtom).title);

export const dialogMessageAtom = atom(
  get => get(confirmationDialogAtom).message
);

export const dialogSentimentAtom = atom(
  get => get(confirmationDialogAtom).sentiment
);

export const dialogConfirmTextAtom = atom(
  get => get(confirmationDialogAtom).confirmText
);

export const dialogCancelTextAtom = atom(
  get => get(confirmationDialogAtom).cancelText
);

export const dialogOnConfirmAtom = atom(
  get => get(confirmationDialogAtom).onConfirm
);

export const dialogOnCancelAtom = atom(
  get => get(confirmationDialogAtom).onCancel
);

export const dialogIsLoadingAtom = atom(
  get => get(confirmationDialogAtom).isLoading
);

// Action atoms for updating the dialog state
export const openDialogAtom = atom(
  null,
  (get, set, newState: Partial<ConfirmationDialogState>) => {
    const currentState = get(confirmationDialogAtom);
    set(confirmationDialogAtom, {
      ...currentState,
      ...newState,
      isOpen: true,
    });
  }
);

export const closeDialogAtom = atom(null, (get, set) => {
  set(confirmationDialogAtom, {
    ...get(confirmationDialogAtom),
    isOpen: false,
  });
});

export const setDialogLoadingAtom = atom(
  null,
  (get, set, isLoading: boolean) => {
    set(confirmationDialogAtom, {
      ...get(confirmationDialogAtom),
      isLoading,
    });
  }
);
